import { useCallback, useEffect, useMemo, useState } from "react";
import { usePost } from "./API";
import { categorizeSignedURLs, snakeToEnglish, fileNameFromUrl } from "./Util";
import { DataTable, Modal, Badge, Spinner, useToast } from "@aidkitorg/component-library";
import { TableAction } from "@aidkitorg/component-library/dist/types/src/DataTable";
import { ColumnDef } from "@tanstack/react-table";
interface FileHubDocument {
  url: string;
  created_at: string;
  source: string;
  target_field: string | null;
  latest: boolean | null;
}

export function FileHub(props: { applicant?: string }) {
  const getDocuments = usePost("/applicant/get_documents");
  const getSignedURL = usePost("/v2/document/view");
  const [documents, setDocuments] = useState<FileHubDocument[]>([]);
  const [loading, setLoading] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalLoading, setModalLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  const { toast } = useToast();
  const [historyModalOpen, setHistoryModalOpen] = useState(false);

  useEffect(() => {
    if (!props.applicant) return;
    (async () => {
      try {
        setLoading(true);
        const result = await getDocuments({ applicant: props.applicant! });
        setDocuments(result?.documents ?? []);
        setLoading(false);
      } catch (e) {
        console.error("Failed to fetch documents:", e);
        toast({ description: `Failed to fetch documents: ${e}`, variant: 'error' });
      } finally {
        setLoading(false);
      }
    })();
  }, [props.applicant]);

  const openFile = useCallback(async (url: string) => {
    setModalTitle(fileNameFromUrl(url));
    setModalOpen(true);
    setModalLoading(true);
    setImageUrl(null);
    setVideoUrl(null);
    setAudioUrl(null);

    try {
      const result = await getSignedURL({ paths: [url] });
      if (result?.paths?.[url]) {
        const { imageFileUrls, videoFileUrls, audioFileUrls } = categorizeSignedURLs(result.paths[url]);
        setImageUrl(imageFileUrls[0]);
        setVideoUrl(videoFileUrls[0]);
        setAudioUrl(audioFileUrls[0]);
      }
    } catch (error) {
      console.error("Failed to fetch signed URL:", error);
    } finally {
      setModalLoading(false);
    }
  }, []);

  const columns = useMemo<ColumnDef<FileHubDocument, any>[]>(() => [
    {
      id: "file",
      header: "File",
      accessorFn: (row) => fileNameFromUrl(row.url),
      cell: ({ row }) => (
        <button
          onClick={() => openFile(row.original.url)}
          className="text-indigo-600 hover:text-indigo-800 underline break-all text-left cursor-pointer bg-transparent border-none p-0"
        >
          {fileNameFromUrl(row.original.url)}
        </button>
      ),
    },
    {
      id: "source",
      header: "Source",
      accessorFn: (row) => row.source,
    },
    {
      id: "target_field",
      header: "Target Field",
      accessorFn: (row) => row.target_field ? snakeToEnglish(row.target_field) : "--",
      cell: ({ row }) => row.original.target_field ? <div className="flex items-center gap-2">
        <code className="text-black">{row.original.target_field}</code>
        {row.original.latest ? <Badge variant="success">Current</Badge> : <></>}
      </div> : "--",
    },
    {
      id: "created_at",
      header: "Last modified",
      accessorFn: (row) => row.created_at,
      cell: ({ row }) => {
        const date = new Date(row.original.created_at);
        return isNaN(date.getTime()) ? row.original.created_at : date.toLocaleString();
      },
    }
  ], [openFile]);

  const actions = useMemo<TableAction<FileHubDocument>[]>(() => [
    {
      label: "View File",
      onClick: (row: FileHubDocument) => openFile(row.url),
      icon: 'EyeIcon',
    },
    {
      label: "Copy To",
      onClick: () => {},
      icon: 'DocumentDuplicateIcon',
    },
    {
      label: "History",
      onClick: () => setHistoryModalOpen(true),
      icon: 'ClockIcon',
    },
    {
      label: "Remove From Attachment",
      onClick: () => {},
      icon: 'XMarkIcon',
      hideWhen: (row: FileHubDocument) => !row.latest,
      disableWhen: (row: FileHubDocument) => {
        if (!row.target_field) return true;
        return documents.filter(d => d.target_field === row.target_field && d.latest).length <= 1;
      },
    },
  ], [openFile, documents]);

  if (loading) {
    return (
      <div className="p-4">
        <h1 className="text-2xl font-bold mb-4">File Hub</h1>
        <Spinner size="large" />
      </div>
    );
  }

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-4">File Hub</h1>
      {documents.length === 0 ? (
        <p className="text-gray-500">No documents found for this applicant.</p>
      ) : (
        <DataTable
          columns={columns}
          data={documents}
          actions={actions}
          getRowId={(row) => `${row.target_field}-${row.url}`}
          showPagination={true}
          enableSearch={true}
        />
      )}

      <Modal
        open={modalOpen}
        onClose={setModalOpen}
        title={modalTitle}
        size="lg"
      >
        {modalLoading ? (
          <p className="text-gray-500 py-8 text-center">Loading file...</p>
        ) : (
          <div className="overflow-auto">
            {videoUrl && (
              <video className="w-full" controls crossOrigin="anonymous" src={videoUrl} />
            )}
            {audioUrl && (
              <audio className="w-full" controls preload="auto">
                <source src={audioUrl} type="audio/mpeg" />
              </audio>
            )}
            {imageUrl && (
              <img alt={modalTitle} className="max-w-full" src={imageUrl} />
            )}
            {imageUrl === null && videoUrl === null && audioUrl === null && (
              <p className="text-gray-500 py-8 text-center">Unable to load file preview.</p>
            )}
          </div>
        )}
      </Modal>

      <Modal
        open={historyModalOpen}
        onClose={setHistoryModalOpen}
        title="File History"
        size="lg"
      >
        <p className="text-gray-500 py-8 text-center">File History</p>
      </Modal>
    </div>
  );
}
